import React from 'react';
import { T, field as themeField, caption } from './speedTheme';
import MonsterSelectDesktop from './Desktop/MonsterSelect';
import MonsterSelectMobile from './Mobile/MonsterSelect';
import { useBackHandler } from '../hooks/useBackHandler';

/**
 * 공속 탭 화면들이 같이 쓰는 조각.
 *
 * 몹을 골라 기본속을 읽는 규칙과 한 줄 입력칸의 생김새가 [프리셋]·[점령용] 두 곳에 필요하다.
 * 두 곳에 따로 두면 한쪽만 고쳐지기 시작한다.
 */

/** 화면에 두는 몹 한 칸 */
/** 리더 스킬 (몹 표의 stats.leader 그대로) */
export interface Leader {
    attribute: string;
    amount: number;
    area: string;
    element: string | null;
}

/** 스킬 요약 (몹 표의 stats.skills 그대로) */
export interface SkillInfo {
    id: number;
    slot: number;
    name: string;
    cooltime: number | null;
    passive: boolean;
    gauge: number;
    gaugeNote: string;
    speedUp: boolean;
}

export interface SlotState {
    name: string;
    /** 스킬 요약. 선턴 자리에 있을 때 게증 %·방식을 채우는 데 쓴다 */
    skills: SkillInfo[];
    /** 속성 — 속성 한정 리더가 이 몹에 먹는지 볼 때 */
    attribute: string;
    /** 리더 스킬. 선턴 자리에 있을 때 리더 % 를 채우는 데 쓴다 */
    leader: Leader | null;
    /** 몹 그림 (매핑의 filepath, 윈도우식 구분자 그대로) */
    imgPath: string;
    /** 몹 고유 기본속 */
    base: string;
    /** 룬으로 오른 속도 (정보창의 +N) */
    flat: string;
    /** 공속아티 % (뒤 몹에만) */
    arti: string;
}

export const BLANK_SLOT: SlotState = { name: '', skills: [], attribute: '', leader: null, imgPath: '', base: '', flat: '', arti: '' };

export const num = (v: string | number | undefined): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

/**
 * 고른 몹의 **기본 공속**을 몹 표에서 읽는다.
 *
 * 고르기 창은 성급까지만 알려 주므로 그림을 고를 때와 같은 차례로 찾는다 —
 * 2차각성 → 각성 → 미각성. 공속은 각성해도 그대로인 몹이 대부분이지만 다른 몹이 있다.
 */
export function baseSpeedOf(mapping: any, name: string, attribute: string): number | null {
    const byAttr = mapping?.monsters?.[name]?.[attribute];
    if (!byAttr) return null;
    for (const state of ['SecondAwakened', 'Awakened', 'Unawaken']) {
        const sp = byAttr[state]?.stats?.speed;
        if (Number.isFinite(sp)) return Number(sp);
    }
    return null;
}

/**
 * **몹의 이름 중 가장 짧은 것.** 상자 아래·표에 적는 이름이다.
 *
 * 한 몹에 이름이 여럿이다(별칭 표: "icaru", "물이누가미", "이카루"). 화면이 좁아 제일 짧은
 * 것을 쓴다. 길이가 같으면 한글을 앞세운다 — 영문 별칭은 검색용이지 부르는 이름이 아니다.
 */
export function shortestName(mapping: any, name: string, attribute: string, fallback: string): string {
    const byAttr = mapping?.monsters?.[name]?.[attribute];
    const aliases: string[] = Array.isArray(byAttr?.aliases) ? byAttr.aliases.filter((a: any) => typeof a === 'string' && a.trim()) : [];
    if (!aliases.length) return fallback;
    const isKo = (t: string) => /[가-힣]/.test(t);
    return [...aliases].sort((a, b) => (a.length - b.length) || (Number(isKo(b)) - Number(isKo(a))))[0];
}

/** 리더 스킬도 같은 차례로 읽는다 (각성하면 리더가 생기는 몹이 있어 각성 쪽을 먼저 본다) */
export function leaderOf(mapping: any, name: string, attribute: string): Leader | null {
    const byAttr = mapping?.monsters?.[name]?.[attribute];
    if (!byAttr) return null;
    for (const state of ['SecondAwakened', 'Awakened', 'Unawaken']) {
        const l = byAttr[state]?.stats?.leader;
        if (l && l.attribute) return l as Leader;
    }
    return null;
}

/** 스킬 요약도 같은 차례로 읽는다 */
export function skillsOf(mapping: any, name: string, attribute: string): SkillInfo[] {
    const byAttr = mapping?.monsters?.[name]?.[attribute];
    if (!byAttr) return [];
    for (const state of ['SecondAwakened', 'Awakened', 'Unawaken']) {
        const s = byAttr[state]?.stats?.skills;
        if (Array.isArray(s) && s.length) return s as SkillInfo[];
    }
    return [];
}

/**
 * **선턴 몹이 뒤 몹에게 무엇을 주나** — 스킬에서 게증 %와 속업을 뽑는다.
 *
 * 게증은 스킬들 중 가장 큰 값. 조건이 붙은 것("Per buff removed" 같은)은 그대로 알려 주고
 * 값은 넣는다 — 그 조건이 성립하는 조합으로 쓰는 것이 보통이라서다. 방식은 둘 다 있으면
 * 게증+속업, 하나면 그것, 없으면 null(사람이 고른다).
 */
export function siegeSkillHint(skills: SkillInfo[]): { gauge: number; note: string; speedUp: boolean; mode: 'gauge' | 'buff' | 'both' | null } {
    let gauge = 0, note = '', speedUp = false;
    for (const s of skills) {
        if (s.gauge > gauge) { gauge = s.gauge; note = s.gaugeNote; }
        if (s.speedUp) speedUp = true;
    }
    const mode = gauge > 0 && speedUp ? 'both' : gauge > 0 ? 'gauge' : speedUp ? 'buff' : null;
    return { gauge, note, speedUp, mode };
}

/**
 * **패시브로 속도가 붙는 몹** — 룬속처럼 그대로 더해진다. 자리(선턴·뒤)와 상관없이 붙는다.
 *   물호박  걸린 버프 수만큼
 *   암헬하  패시브 Spurt — 공속 +N (사용자 2026-10-28). 체력이 깎일수록 더 오르지만 전투 시작은 체력이 가득이라 N 만.
 *           2차각성은 +25, 1차각성은 +15 다. 계산기는 기본속·스킬을 2차각성부터 읽으므로 2차각성을 먼저 둔다.
 *
 * 스킬 표에서 자동으로 뽑기 어려운 값이라(패시브 설명글에만 있다) 여기 적어 둔다. 둥봇도
 * 같은 값을 손으로 두고 있다. 새 몹이 생기면 줄을 더한다. 맨 앞 칸이 기본값이다.
 */
export interface PassiveSpeed {
    /** 고르는 칸 위의 이름표 — "(걸린 버프 수)" 같은 것 */
    title: string;
    options: Array<{ label: string; bonus: number }>;
}

const PASSIVE_SPEED: Record<string, PassiveSpeed> = {
    '물호박': { title: '걸린 버프 수', options: [{ label: '버프 없음', bonus: 0 }, { label: '버프 1개', bonus: 19.5 }, { label: '버프 2개', bonus: 39 }] },
    '암헬하': { title: '패시브', options: [{ label: '2차각성', bonus: 25 }, { label: '1차각성', bonus: 15 }] }
};

export function passiveOptionsOf(koreanName: string): PassiveSpeed | null {
    return PASSIVE_SPEED[koreanName] || null;
}

/**
 * **점령전에서 먹는 공격속도 리더인가.** 먹으면 몇 %인지 돌려준다.
 *
 * `area` 가 Arena·Dungeon 이면 점령전에서 안 먹는다. 속성 한정(element) 리더는 뒤 몹이
 * 모두 그 속성일 때만 — 하나라도 다르면 채우지 않는다(틀린 숫자보다 빈칸이 낫다).
 */
export function siegeSpeedLeader(l: Leader | null, followerAttrs: string[]): number | null {
    if (!l || l.attribute !== 'Attack Speed') return null;
    if (!['Guild', 'General'].includes(l.area)) return null;
    if (l.element && followerAttrs.some((a) => a && a !== l.element)) return null;
    return l.amount;
}

/** 사이트에서 쓰던 몹 고르기 창 — 데스크톱·모바일 중 맞는 것을 띄운다 (정렬은 창이 알아서 한다) */
export function PickerModal({
    isMobile, monsterMapping, onClose, onPick
}: {
    isMobile: boolean;
    monsterMapping: any;
    onClose: () => void;
    /** 마지막 `family` 는 몹 표의 열쇠(가문 이름) — 짧은 이름만으로는 계정의 몹을 못 찾아서 덧붙였다 */
    onPick: (name: string, base: number | null, imgPath: string, attribute: string, leader: Leader | null, skills: SkillInfo[], family: string) => void;
}) {
    // 뒤로 가기: 떠 있는 동안 늘 열려 있으니 닫기만 하면 된다(공속·룬 탭이 같이 쓴다)
    useBackHandler(true, onClose);
    const Select: any = isMobile ? MonsterSelectMobile : MonsterSelectDesktop;
    return (
        <Select
            open
            monsterMapping={monsterMapping}
            onClose={onClose}
            onSelect={(m: any) => {
                onPick(
                    shortestName(monsterMapping, m.name, m.attribute, m.displayName || m.name),
                    baseSpeedOf(monsterMapping, m.name, m.attribute),
                    m.image_path || '',
                    m.attribute,
                    leaderOf(monsterMapping, m.name, m.attribute),
                    skillsOf(monsterMapping, m.name, m.attribute),
                    m.name
                );
                onClose();
            }}
        />
    );
}

/**
 * 한글 이름(별칭)으로 몹을 찾는다 — 프리셋의 "물호박" 같은 이름을 그림으로 바꿀 때.
 * 그림은 고르기 창과 같은 차례(2차각성 → 각성 → 미각성)로 고른다.
 */
export function findByKoreanName(mapping: any, korean: string): { name: string; attribute: string; imgPath: string; base: number | null } | null {
    const mons = mapping?.monsters;
    if (!mons) return null;
    for (const [name, info] of Object.entries<any>(mons)) {
        for (const attr of ['Water', 'Fire', 'Wind', 'Light', 'Dark']) {
            const byAttr = info?.[attr];
            const aliases: string[] = Array.isArray(byAttr?.aliases) ? byAttr.aliases : [];
            if (!aliases.includes(korean)) continue;
            const img = byAttr.SecondAwakened?.filepath || byAttr.Awakened?.filepath || byAttr.Unawaken?.filepath || '';
            return { name, attribute: attr, imgPath: img, base: baseSpeedOf(mapping, name, attr) };
        }
    }
    return null;
}

/**
 * **몹 상자** — 전적검색 탭의 칸과 같다.
 *
 * 70px 흰 칸, 고르면 보라 테두리에 그림, 비어 있으면 점선에 큰 +. 위에 자리 이름, 오른쪽 위에
 * 파란 띠(선턴), 왼쪽 위에 × 로 비운다. 이름은 적지 않는다 — 그림이 곧 이름이다.
 * `onClick` 이 없으면(프리셋의 정해진 몹) 누를 수 없고 × 도 없다.
 */
export function MonsterBox({
    imgPath, name, label, badge, onClick, onClear, size = 70, compact = false, pulse = false
}: {
    imgPath: string;
    name?: string;
    /** 상자 위 작은 글자 (전적검색의 LEADER 자리). 줄을 맞추려고 없을 때도 자리를 둔다 */
    label?: string;
    /** 상자 **오른쪽 위** 파란 띠 — "선턴" */
    badge?: string;
    onClick?: () => void;
    onClear?: () => void;
    size?: number;
    /** 좁은 화면 — 전적검색과 같은 clamp 크기로 줄인다 */
    compact?: boolean;
    /** 다음에 고를 칸 — 전적검색과 같은 파란 깜박임(App.css 의 pick-slot-pulse) */
    pulse?: boolean;
}) {
    const clickable = !!onClick;
    const filled = !!imgPath;
    /* 전적검색 모바일과 같은 치수다. 숫자를 바꿀 땐 Mobile/CounterList 도 같이 봐야 한다 */
    const boxSize = compact ? 'clamp(56px, 11vw, 60px)' : size;
    const radius = compact ? 'clamp(12px, 2.5vw, 14px)' : 12;
    const innerSize = compact ? 'clamp(50px, 10vw, 54px)' : size - 7;
    const imgSize = compact ? 'clamp(52px, 10.5vw, 56px)' : size - 5;
    const plusSize = compact ? 'clamp(30px, 6vw, 34px)' : 32;
    const clearSize = compact ? 'clamp(22px, 4.5vw, 24px)' : 24;
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            {/* 상자 위 자리 이름 — 없을 때도 줄 높이는 지킨다 */}
            <span style={{
                fontSize: 10, fontWeight: 800, letterSpacing: 0.5, lineHeight: 1, userSelect: 'none',
                color: label ? '#667eea' : 'transparent'
            }}>{label || 'X'}</span>
            <div
                title={name || (clickable ? '몹 고르기' : undefined)}
                onClick={onClick}
                className={pulse ? 'pick-slot-pulse' : undefined}
                style={{
                    width: boxSize, height: boxSize, background: '#fff', borderRadius: radius, position: 'relative',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: filled ? '2px solid #667eea' : '2px dashed #ddd',
                    cursor: clickable ? 'pointer' : 'default', transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => { if (clickable) { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; } }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'; }}
            >
                {badge && (
                    <span style={{
                        position: 'absolute', top: -8, right: -8, background: '#1976d2', color: '#fff', borderRadius: 999,
                        padding: '2px 8px', fontSize: 10, fontWeight: 800, boxShadow: '0 2px 6px rgba(0,0,0,0.25)', zIndex: 2
                    }}>{badge}</span>
                )}
                {filled ? (
                    <>
                        <div style={{ width: innerSize, height: innerSize, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: radius, overflow: 'hidden' }}>
                            <img loading="lazy"
                                src={'/mobs/v2/' + imgPath.replace(/\\/g, '/')}
                                alt={name || ''}
                                style={{ width: imgSize, height: imgSize, objectFit: 'contain', borderRadius: radius }}
                            />
                        </div>
                        {onClear && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onClear(); }}
                                title="비우기"
                                style={{
                                    position: 'absolute', top: -8, left: -8, background: '#ff6b6b', border: 'none', borderRadius: '50%',
                                    width: clearSize, height: clearSize, color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}
                            >×</button>
                        )}
                    </>
                ) : (
                    <span style={{ fontSize: plusSize, color: '#ccc', userSelect: 'none', fontWeight: 300 }}>+</span>
                )}
            </div>
        </div>
    );
}

/** 숫자만 받는 칸 */
export function NumInput({
    value, onChange, max = 3, font = 14, placeholder
}: {
    value: string;
    onChange: (v: string) => void;
    max?: number;
    font?: number;
    placeholder?: string;
}) {
    return (
        <input
            value={value}
            onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, '').slice(0, max))}
            inputMode="numeric"
            placeholder={placeholder}
            style={{ ...themeField, fontSize: font, padding: '8px 10px' }}
        />
    );
}

/** 칸 위의 작은 이름표 */
export function Cap({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) {
    return (
        <div style={{ ...caption, display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span>{children}</span>
            {right && <span style={{ marginLeft: 'auto' }}>{right}</span>}
        </div>
    );
}

/** 작은 글자 단추 (고르기 등) */
export function LinkButton({ onClick, children, color = T.gold }: { onClick: () => void; children: React.ReactNode; color?: string }) {
    return (
        <button
            onClick={onClick}
            style={{ border: 'none', background: 'none', padding: 0, color, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
        >{children}</button>
    );
}

/** 판정 칩 — "충분" 이거나 모자란 룬속 */
export function ShortChip({ short, font = 14 }: { short: number; font?: number }) {
    const ok = short === 0;
    return (
        <span style={{
            padding: '6px 16px', borderRadius: 999, fontWeight: 800, fontSize: font + 2,
            fontVariantNumeric: 'tabular-nums',
            background: ok ? 'rgba(46,125,50,0.10)' : 'rgba(211,47,47,0.10)',
            color: ok ? T.green : T.red
        }}>
            {ok ? '충분' : '+' + short}
        </span>
    );
}
